import { FeatureCollection } from 'geojson';
import { XMLParser } from 'fast-xml-parser';
import { uniq } from 'lodash';
import { showError } from './toast';

const SDM_POST_REST =
  'https://sdmdataaccess.sc.egov.usda.gov/Tabular/post.rest';

const POST_OPTIONS = {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
};

const CREATE_AOI = { service: 'aoi', request: 'create' };
const INTERP_DESC = { service: 'interpretation', request: 'getdescription' };
const REPORT_DESC = { service: 'report', request: 'getdescription' };
const INTERP_RATING = { service: 'interpretation', request: 'getrating' };
const GET_REPORT = { service: 'report', request: 'getreport' };
export const QUERY_SDM = { service: 'query', request: 'query', format: 'json' };
const XML_DESC_PARSER = new XMLParser({
  preserveOrder: true,
  ignoreDeclaration: true,
});
const XML_REPORT_PARSER = new XMLParser({ ignoreDeclaration: true });

const thematicMapBody = (
  type: '' | 'Legend' | 'Rating',
  interpresultid: string,
) => ({
  ...QUERY_SDM,
  // eslint-disable-next-line no-useless-escape
  query: `SELECT * FROM SDA_Get_AoiSoilThematicMap${type}_By_AoiSoilThematicMapId(\'${interpresultid}\')`,
});

const fuzzyLogicBody = (mapUnitKeys: string[], ruleName: string) => ({
  ...QUERY_SDM,
  query: `
    SELECT saversion, saverest, l.areasymbol, l.areaname, l.lkey, musym, muname, museq, mu.mukey, comppct_r, compname, localphase, slope_r, c.cokey, mrulename, ruledesign, ruledepth, ci.seqnum, interphr, interphrc 
    FROM sacatalog sac 
    INNER JOIN legend l ON l.areasymbol = sac.areasymbol
    INNER JOIN mapunit mu ON mu.lkey = l.lkey AND mu.mukey IN ('${mapUnitKeys.join(
      "', '",
    )}') 
    LEFT OUTER JOIN component c ON c.mukey = mu.mukey 
    LEFT OUTER JOIN cointerp ci ON ci.cokey = c.cokey 
    WHERE mrulename = '${ruleName}'
    ORDER BY l.areaname, museq, comppct_r DESC, compname, c.cokey, mrulename, ci.seqnum
    `,
});

export const sendSdmPostRest = <T>(
  body: { [key: string]: string },
  errorResult: Nullable<T> = null,
  parseJson: boolean = true,
) =>
  fetch(SDM_POST_REST, { ...POST_OPTIONS, body: JSON.stringify(body) })
    .then((response) => (parseJson ? response.json() : response.text()))
    .catch((e) => {
      if (e instanceof SyntaxError && e.message?.includes('not valid JSON')) {
        showError('Unable to parse soil data response');
      } else {
        showError(e);
      }
      return errorResult;
    });

export const createAoi = (features: FeatureCollection) => {
  if (!features.features.length) return Promise.resolve(undefined);
  const body = {
    ...CREATE_AOI,
    aoicoords: JSON.stringify(features),
  };
  return sendSdmPostRest(body).then(
    (json: Nullable<{ id: string }>) => json?.id,
  );
};

export const getInterpretationDescription = (
  attributekey: Nullable<string>,
) => {
  if (!attributekey) return Promise.resolve(undefined);
  const body = {
    ...INTERP_DESC,
    attributekey,
  };
  return sendSdmPostRest(body).then(
    (json: Nullable<{ description: string }>) => json?.description,
  );
};

export const getReportDescription = (reportid: Nullable<string>) => {
  if (!reportid) return Promise.resolve(undefined);
  const body = {
    ...REPORT_DESC,
    reportid,
  };
  return sendSdmPostRest(body, null, false).then((xml: Nullable<string>) => {
    if (!xml) return null;
    return XML_DESC_PARSER.parse(xml);
  });
};

export const getInterpretationRating = (
  aoiid: Nullable<string>,
  shortFormData: Nullable<{ [key: string]: string }>,
) => {
  if (!aoiid || !shortFormData) return Promise.resolve(undefined);
  const body = {
    ...INTERP_RATING,
    shortformdata: JSON.stringify({
      aoiid: aoiid.toString(),
      ...shortFormData,
    }),
  };
  return sendSdmPostRest(body).then(
    (json: Nullable<{ interpresultid: string }>) => json?.interpresultid,
  );
};

export const getReport = (
  aoiid: Nullable<string>,
  shortFormData: Nullable<{ [key: string]: string }>,
) => {
  if (!aoiid || !shortFormData) return Promise.resolve(undefined);
  const body = {
    ...GET_REPORT,
    shortformdata: JSON.stringify({
      aoiid: aoiid.toString(),
      ...shortFormData,
    }),
  };
  return sendSdmPostRest(body, null, false).then((xml: Nullable<string>) => {
    if (!xml) return null;
    return XML_REPORT_PARSER.parse(xml);
  });
};

export const getThematicMap = (
  interpresultid: Nullable<string>,
  type: '' | 'Legend' | 'Rating' = '',
) => {
  if (!interpresultid) return Promise.resolve();
  return sendSdmPostRest(thematicMapBody(type, interpresultid));
};

export const getThematicMapLegend = (interpresultid: Nullable<string>) =>
  getThematicMap(interpresultid, 'Legend');

export const getThematicMapRating = (interpresultid: Nullable<string>) =>
  getThematicMap(interpresultid, 'Rating');

export const getFuzzyLogicInterpretation = (
  featureCollection: Nullable<FeatureCollection>,
  ruleName: Nullable<string>,
) => {
  if (featureCollection == null || ruleName == null) return Promise.resolve();
  const mapUnitKeys = uniq(
    featureCollection.features.flatMap((f) =>
      f.properties ? [f.properties.mapUnitKey] : [],
    ),
  );
  // Ensure there is at least one map unit key
  if (!mapUnitKeys.length) return Promise.resolve();
  return sendSdmPostRest(fuzzyLogicBody(mapUnitKeys, ruleName));
};
